import * as Tone from 'tone';
import { createMixBus } from './playbackMix';
import audioEngine from './audioEngine';
import eqProcessor from './eqProcessor';
import { markPlaybackActive, markPlaybackIdle } from './playbackActivity';

const progressionSignature = (chords) => {
  if (!Array.isArray(chords)) return '';
  return chords
    .map((chord) => [
      chord?.note || '',
      chord?.quality || '',
      chord?.rootNote || '',
      chord?.mode || ''
    ].join(':'))
    .join('|');
};

const randomPoolSignature = (pool) => {
  if (!Array.isArray(pool)) return '';
  return pool.map((prog) => `${prog?.name || ''}>${progressionSignature(prog?.chords)}`).join('#');
};

/**
 * SynthPlayer - Unified audio player with Transport-based timing
 * 
 * Replaces the fragmented timing system with a DAW-inspired approach:
 * - Single Transport timeline as master clock
 * - Tone.Loop for metronome clicks (synced to Transport)
 * - Tone.Part for chord progression sequences
 * - Perfect synchronization between all timed audio
 */
class SynthPlayer {
  constructor() {
    this.playbackState = 'stopped';
    this.isPlaying = false;
    this.bpm = 120;
    this.timeSignature = '4/4';
    this.barsPerChord = 4;
    
    // Audio components
    this.metronomeLoop = null;
    this.progressionPart = null;
    this.clickOscillator = null;
    this.accentOscillator = null;
    this.clickEnvelope = null;
    this.accentEnvelope = null;
    this.gainNode = null;
    
    // State tracking
    this.currentChord = null;
    this.chordProgression = [];
    this.progressionSignature = null;
    this.randomPoolSignature = null;
    this.metronomeEnabled = true;
    this.metronomeVisualOnly = false;
    this.metronomeVolume = 0.7;
    this.currentBeat = 0;
    this.beatsPerBar = 4;
    this.beatSubdivision = 4; // Quarter note = 4
    this.progressionIndex = 0;
    this.hasStartedChords = false; // Track if first chord has been played

    // Infinite random-progression mode: instead of one fixed looping sequence,
    // draw a short cadence from a pool, loop it randomLoops times, then move to a
    // different one (never the same twice in a row). Each chord is held for
    // barsPerChord bars, like a normal progression. randomLoops = Infinity keeps a
    // single progression looping forever (the "easiest/most predictable" end).
    this.randomMode = false;
    this.randomPool = [];          // [{ name, chords: [chordObj, ...] }]
    this.randomLoops = 4;          // n: times to repeat a progression before switching
    this.lastRandomKey = null;     // name of the progression playing, to avoid repeats
    this.randomProgression = null; // current { name, chords }
    this.randomChordIndex = 0;     // position within the current progression
    this.randomLoopCount = 0;      // completed loops of the current progression
    
    // Callbacks for UI updates
    this.callbacks = {
      onChordChange: [],
      onBeat: [],
      onBar: [],
      onPlayingStateChange: []
    };
    
    // Transport synchronization
    this.transportStartTime = null;
    this.initialized = false;
  }

  /**
   * Initialize the SynthPlayer
   * Sets up audio components and prepares Transport
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Ensure Tone.js is started
      await Tone.start();

      // Make sure audio context is running
      const context = Tone.getContext();
      if (context.state !== 'running') {
        await context.resume();
      }

      // Increase lookAhead for better stability on Bluetooth devices
      // Default is 0.1s, increase to 0.2s to prevent crackling/underruns
      context.lookAhead = 0.2;

      this.setupAudioComponents();
      this.parseTimeSignature(this.timeSignature);

      this.initialized = true;
    } catch (error) {
      console.error('[SynthPlayer] Error initializing:', error);
      throw error;
    }
  }

  /**
   * Set up audio components for metronome sounds
   */
  setupAudioComponents() {
    // Create main gain node for volume control (routed through the global EQ)
    this.mixBus = createMixBus('metronome').connect(eqProcessor.getInput());
    this.gainNode = new Tone.Gain(this.metronomeVolume).connect(this.mixBus);
    
    // Create click sound (regular beats)
    this.clickOscillator = new Tone.Oscillator({
      frequency: 800,
      type: 'sine'
    });
    
    this.clickEnvelope = new Tone.AmplitudeEnvelope({
      attack: 0.001,
      decay: 0.1,
      sustain: 0,
      release: 0.1
    }).connect(this.gainNode);
    
    this.clickOscillator.connect(this.clickEnvelope);
    this.clickOscillator.start();
    
    // Create accent sound (downbeats)
    this.accentOscillator = new Tone.Oscillator({
      frequency: 1200,
      type: 'sine'
    });
    
    this.accentEnvelope = new Tone.AmplitudeEnvelope({
      attack: 0.001,
      decay: 0.15,
      sustain: 0,
      release: 0.15
    }).connect(this.gainNode);
    
    this.accentOscillator.connect(this.accentEnvelope);
    this.accentOscillator.start();
  }

  /**
   * Parse time signature string (e.g., "4/4", "3/4", "6/8")
   */
  parseTimeSignature(timeSignature) {
    const [numerator, denominator] = timeSignature.split('/').map(Number);
    
    this.beatsPerBar = numerator;
    this.beatSubdivision = denominator;
    
    // Keep Tone.getTransport() in sync with our time signature
    try {
      Tone.getTransport().timeSignature = [numerator, denominator];
    } catch (e) {
      console.warn('[SynthPlayer] Failed to set Transport.timeSignature', e);
      try { 
        Tone.getTransport().timeSignature = numerator; 
      } catch (_) {}
    }
  }

  /**
   * Set the playback state ('stopped' | 'playing' | 'paused').
   * isPlaying stays the historical boolean: true only while actually playing.
   */
  setPlaybackState(state) {
    this.playbackState = state;
    this.isPlaying = state === 'playing';
  }

  /**
   * Master play control - starts everything with sample-accurate timing.
   * From a paused state, resumes at the kept position instead of restarting.
   */
  async play() {
    if (!this.initialized) {
      await this.initialize();
    }

    if (this.playbackState === 'playing') {
      return;
    }

    if (this.playbackState === 'paused') {
      return this.resume();
    }

    this.setPlaybackState('playing');
    this.currentBeat = 0;
    this.progressionIndex = 0;

    // Ensure Transport is stopped and cleaned up before starting
    if (Tone.getTransport().state !== 'stopped') {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
    }

    // Set BPM on Transport
    Tone.getTransport().bpm.value = this.bpm;

    // Prepare metronome and progression before starting Transport
    this.setupMetronome();
    if (this.chordProgression.length > 0 || this.randomMode) {
      this.setupProgression();
    }

    // Reliable immediate start strategy for Tone.js scheduling:
    // - Start parts/loops at position 0 so events at 0m don't get missed
    // - Then start Transport slightly in the future to allow scheduling
    const startDelaySeconds = 0.05; // small scheduling lookahead
    this.transportStartTime = Tone.getTransport().now() + startDelaySeconds;

    // Reset Transport timeline to the beginning
    try {
      Tone.getTransport().position = 0;
    } catch (_) {}

    // Start metronome loop at 0 (relative to Transport)
    if (this.metronomeLoop) {
      try { this.metronomeLoop.stop(); } catch (_) {}
      this.metronomeLoop.start(0);
    }

    // Start progression part at 0 (relative to Transport)
    if (this.progressionPart) {
      try { this.progressionPart.stop(); } catch (_) {}
      this.progressionPart.start(0);
    }

    // Start Transport slightly in the future for stable scheduling
    Tone.getTransport().start(`+${startDelaySeconds}`);

    markPlaybackActive('synthPlayer');

    // Notify callbacks
    this.triggerPlayingStateCallbacks(true);

  }

  /**
   * Pause - the Transport keeps its position (current chord, bar, beat) and
   * nothing sounds any more. The Loop/Part are kept alive so the resume picks
   * up exactly where it left off.
   */
  pause() {
    if (this.playbackState !== 'playing') {
      return;
    }

    try {
      Tone.getTransport().pause();
    } catch (error) {
      console.error('[SynthPlayer] Error pausing Transport:', error);
    }

    try {
      audioEngine.stopChord();
    } catch (error) {
      console.error('[SynthPlayer] Error stopping chords on pause:', error);
    }
    this.hasStartedChords = false;

    this.setPlaybackState('paused');

    markPlaybackIdle('synthPlayer');

    this.triggerPlayingStateCallbacks(false);
  }

  /**
   * Resume at the position kept by pause, striking again the chord that was
   * sounding back then (its Part event has already fired).
   */
  async resume() {
    if (this.playbackState !== 'paused') {
      return;
    }

    if (!this.initialized) {
      await this.initialize();
    }

    const startDelaySeconds = 0.05;
    const resumeTime = Tone.getTransport().now() + startDelaySeconds;

    Tone.getTransport().bpm.value = this.bpm;
    Tone.getTransport().start(`+${startDelaySeconds}`);

    this.setPlaybackState('playing');

    markPlaybackActive('synthPlayer');

    if (this.currentChord) {
      this.switchChord(this.currentChord, resumeTime);
    }

    this.triggerPlayingStateCallbacks(true);
  }

  /**
   * Drop a kept pause position: the next play starts from the beginning.
   */
  resetPausedPosition() {
    if (this.playbackState === 'paused') {
      this.stop();
    }
  }

  /**
   * Master stop control - stops everything immediately
   */
  stop() {
    if (this.playbackState === 'stopped') {
      return;
    }

    const wasPaused = this.playbackState === 'paused';
    this.setPlaybackState('stopped');

    if (wasPaused) {
      try {
        Tone.getTransport().stop();
        Tone.getTransport().cancel();
      } catch (error) {
        console.error('[SynthPlayer] Error stopping Transport:', error);
      }
    }

    // Stop and dispose of loops/parts
    if (this.metronomeLoop) {
      this.metronomeLoop.stop();
      this.metronomeLoop.dispose();
      this.metronomeLoop = null;
    }

    if (this.progressionPart) {
      this.progressionPart.stop();
      this.progressionPart.dispose();
      this.progressionPart = null;
    }

    // Stop any playing chords
    this.stopChords();

    // Reset state
    this.currentBeat = 0;
    this.progressionIndex = 0;
    this.transportStartTime = null;

    markPlaybackIdle('synthPlayer');

    // Notify callbacks
    this.triggerPlayingStateCallbacks(false);

  }

  /**
   * Setup metronome using Transport-synced Tone.Loop
   */
  setupMetronome() {
    if (this.metronomeLoop) {
      this.metronomeLoop.dispose();
    }

    const beatInterval = `${this.beatSubdivision}n`; // e.g., "4n" for quarter notes
    
    this.metronomeLoop = new Tone.Loop((time) => {
      this.playBeat(time);
    }, beatInterval);

  }

  /**
   * Setup chord progression using Transport-synced Tone.Part
   */
  setupProgression() {
    
    // Clean up existing part
    if (this.progressionPart) {
      this.progressionPart.dispose();
      this.progressionPart = null;
    }

    // Infinite random-progression mode: a Loop fires once per chord slot
    // (barsPerChord bars). advanceRandom walks the current cadence, loops it
    // randomLoops times, then jumps to a different progression.
    if (this.randomMode && this.randomPool.length > 0) {
      this.randomProgression = null;
      this.randomChordIndex = 0;
      this.randomLoopCount = 0;
      this.progressionPart = new Tone.Loop((time) => {
        this.advanceRandom(time);
      }, `${this.barsPerChord}m`);
      this.progressionPart.loop = true;
      return;
    }

    if (!this.chordProgression || this.chordProgression.length === 0) {
      return;
    }
    
    // Calculate the events based on bars per chord
    const events = [];
    this.chordProgression.forEach((chord, index) => {
      const measureTime = index * this.barsPerChord;
      events.push({
        time: `${measureTime}m`,
        chord,
        index
      });
    });
    
    // Create the Part with the scheduled events
    this.progressionPart = new Tone.Part((time, event) => {
      const transportTime = Tone.getTransport().position;
      
      this.progressionIndex = event.index;
      this.switchChord(event.chord, time);
      // Pass the event with index to callbacks so they know which chord is playing
      this.triggerChordChangeCallbacks(event.chord, time, event.index);
    }, events);
    
    // Set up looping - CRITICAL: must set loopEnd and loopStart!
    this.progressionPart.loop = true;
    this.progressionPart.loopEnd = this.barsPerChord * this.chordProgression.length + "m";
    this.progressionPart.loopStart = "0m";
    
  }

  /**
   * Play a beat (used by metronome loop)
   */
  playBeat(time) {
    const isDownbeat = this.currentBeat === 0;

    // Play metronome sound if enabled and not visual-only
    if (this.metronomeEnabled && !this.metronomeVisualOnly) {
      if (isDownbeat) {
        // Accent on the first beat of each bar
        this.accentEnvelope.triggerAttackRelease('0.1', time);
      } else {
        // Regular click for other beats
        this.clickEnvelope.triggerAttackRelease('0.05', time);
      }
    }
    
    // Trigger beat callbacks (always, for visual feedback)
    this.triggerBeatCallbacks(this.currentBeat + 1, isDownbeat, time);
    
    // Trigger bar callbacks on downbeat
    if (isDownbeat) {
      this.triggerBarCallbacks(time);
    }
    
    // Advance to next beat
    this.currentBeat = (this.currentBeat + 1) % this.beatsPerBar;
  }

  /**
   * Switch to a new chord using the synth audio engine
   *
   * IMPORTANT: This method assumes chords are already pre-rendered via audioEngine.setCurrentScale()
   * in App.js. It does NOT trigger re-rendering to avoid on-the-fly synthesis during playback.
   */
  async switchChord(chord, time) {
    // Handle both 'note' and 'rootNote' property names for compatibility
    const noteName = chord.note || chord.rootNote;
    this.currentChord = chord;

    try {
      // If chord has its own root and mode, update the scale context (synth only)
      if (chord.rootNote && chord.mode) {
        if (audioEngine.currentScaleRoot !== chord.rootNote || audioEngine.currentScaleMode !== chord.mode) {
          audioEngine.setCurrentScale(chord.rootNote, chord.mode);
        }
      }

      const useSwitch = this.hasStartedChords;

      // Single engine: synth
      if (useSwitch) {
        await audioEngine.switchChord(noteName, chord.quality, time, chord.scaleRoot);
      } else {
        await audioEngine.playChord(noteName, chord.quality, time, chord.scaleRoot);
        this.hasStartedChords = true;
      }
    } catch (error) {
      console.error(`[SynthPlayer] Error switching to chord ${noteName} ${chord.quality}:`, error);
    }
  }

  /**
   * Stop all chord audio
   */
  stopChords() {
    try {
      audioEngine.stopChord();
      this.hasStartedChords = false;
      this.currentChord = null;
    } catch (error) {
      console.error('[SynthPlayer] Error stopping chords:', error);
    }
  }

  // === Public API Methods ===

  /**
   * Enable/disable metronome clicks (keeps timing sync)
   */
  setMetronomeEnabled(enabled) {
    this.metronomeEnabled = enabled;
  }

  /**
   * Set metronome visual-only mode
   */
  setMetronomeVisualOnly(visualOnly) {
    this.metronomeVisualOnly = visualOnly;
  }

  /**
   * Set metronome volume
   */
  setMetronomeVolume(volume) {
    if (volume < 0 || volume > 1) {
      console.warn('[SynthPlayer] Volume out of range (0-1):', volume);
      return;
    }
    
    this.metronomeVolume = volume;
    if (this.gainNode) {
      this.gainNode.gain.value = volume;
    }
  }

  /**
   * Set chord progression
   */
  setProgression(chords) {
    const signature = progressionSignature(chords);

    // Same chords as before: nothing to do (components call setProgression on
    // every render).
    if (!this.randomMode && signature === this.progressionSignature) {
      return;
    }

    this.progressionSignature = signature;
    this.chordProgression = chords;
    // Choosing a real progression leaves random mode.
    this.randomMode = false;
    this.randomPoolSignature = null;

    this.resetPausedPosition();

    // Rebuild and restart the Part so the new progression takes effect immediately
    if (this.isPlaying) {
      this.setupProgression();
      if (this.progressionPart) {
        this.progressionPart.start(0);
      }
    }
  }

  /**
   * Enable/disable infinite random-progression mode.
   * @param {boolean} enabled
   * @param {Array} pool - progressions ({ name, chords: [chordObj, ...] }) to draw from
   * @param {number} [loops] - n: times to repeat each progression before switching
   *                           (Infinity = loop a single progression forever)
   */
  setRandomMode(enabled, pool = [], loops = null) {
    const wasRandom = this.randomMode;
    this.randomMode = !!enabled;
    this.randomPool = Array.isArray(pool) ? pool : [];
    if (typeof loops === 'number' && loops > 0) {
      this.randomLoops = loops;
    }
    if (!this.randomMode) {
      this.lastRandomKey = null;
      this.randomProgression = null;
      this.randomChordIndex = 0;
      this.randomLoopCount = 0;
      this.randomPoolSignature = null;
      return;
    }

    const signature = randomPoolSignature(this.randomPool);
    const poolChanged = signature !== this.randomPoolSignature;
    this.randomPoolSignature = signature;

    if (!wasRandom || poolChanged) {
      this.resetPausedPosition();
    }
    // Entering random mode while already playing: rebuild the part as a random
    // loop and start it.
    if (this.isPlaying && !wasRandom) {
      this.setupProgression();
      if (this.progressionPart) {
        this.progressionPart.start(0);
      }
      return;
    }
    // Already random and the pool was refreshed (e.g. scale/voicing change):
    // re-resolve the in-flight cadence against the new pool so it keeps playing
    // in the new key. If it is gone, a fresh one is drawn on the next tick.
    if (this.isPlaying && wasRandom && this.randomProgression) {
      const updated = this.randomPool.find(p => p.name === this.randomProgression.name);
      this.randomProgression = updated || null;
      if (!updated) {
        this.randomChordIndex = 0;
        this.randomLoopCount = 0;
      }
    }
  }

  /**
   * Pick a progression from the pool, never the same as the previous one.
   */
  pickRandomProgression() {
    const pool = this.randomPool;
    if (!pool.length) return null;
    if (pool.length === 1) return pool[0];
    let prog, guard = 0;
    do {
      prog = pool[Math.floor(Math.random() * pool.length)];
      guard++;
    } while (prog.name === this.lastRandomKey && guard < 20);
    this.lastRandomKey = prog.name;
    return prog;
  }

  /**
   * Advance the random-progression state machine by one chord slot: strike the
   * current chord, step forward, and once the current cadence has looped
   * randomLoops times, switch to a different one (never the same twice in a row).
   */
  advanceRandom(time) {
    if (!this.randomProgression) {
      this.randomProgression = this.pickRandomProgression();
      this.randomChordIndex = 0;
      this.randomLoopCount = 0;
    }
    const prog = this.randomProgression;
    if (!prog || !Array.isArray(prog.chords) || prog.chords.length === 0) return;

    const chord = prog.chords[this.randomChordIndex];
    this.switchChord(chord, time);
    this.triggerChordChangeCallbacks(chord, time, this.randomChordIndex);

    this.randomChordIndex += 1;
    if (this.randomChordIndex >= prog.chords.length) {
      this.randomChordIndex = 0;
      this.randomLoopCount += 1;
      if (this.randomLoopCount >= this.randomLoops) {
        this.randomProgression = this.pickRandomProgression();
        this.randomChordIndex = 0;
        this.randomLoopCount = 0;
      }
    }
  }

  /**
   * Set BPM (updates Transport immediately if playing)
   */
  setBPM(bpm) {
    if (bpm < 40 || bpm > 200) {
      console.warn('[SynthPlayer] BPM out of range (40-200):', bpm);
      return;
    }
    
    // Avoid unnecessary updates that could corrupt Transport timeline
    if (this.bpm === bpm) {
      return;
    }
    
    this.bpm = bpm;

    this.resetPausedPosition();

    if (this.isPlaying && Tone.getTransport().state === 'started') {
      // Only update if Transport is actually running
      Tone.getTransport().bpm.value = bpm;
    }
    
  }

  /**
   * Set time signature
   */
  setTimeSignature(timeSignature) {
    // Avoid unnecessary updates
    if (this.timeSignature === timeSignature) {
      return;
    }
    
    const wasPlaying = this.isPlaying;

    this.resetPausedPosition();

    if (wasPlaying) {
      // Properly stop everything before changing time signature
      this.stop();
      // Give Transport time to clean up
      Tone.getTransport().cancel();
    }
    
    this.timeSignature = timeSignature;
    this.parseTimeSignature(timeSignature);
    
    if (wasPlaying) {
      // Small delay to ensure Transport is fully stopped before restarting
      setTimeout(() => {
        this.play();
      }, 50);
    }
    
  }

  /**
   * Set bars per chord
   */
  setBarsPerChord(bars) {
    if (bars < 1 || bars > 16) {
      console.warn('[SynthPlayer] Bars per chord out of range (1-16):', bars);
      return;
    }

    // Avoid needless rebuilds (this is also called on unrelated BPM/time-sig changes)
    if (this.barsPerChord === bars) {
      return;
    }

    this.barsPerChord = bars;

    this.resetPausedPosition();

    // Rebuild with new timing if we have a progression, or in random mode whose
    // Loop also fires every barsPerChord bars.
    if (this.chordProgression.length > 0 || this.randomMode) {
      this.setupProgression();
      
      // If playing, restart the Part at the correct time
      if (this.isPlaying && this.progressionPart) {
        this.progressionPart.stop();
        this.progressionPart.start(Tone.getTransport().now());
      }
    }
  }

  // === Callback Management ===

  /**
   * Add chord change callback
   */
  onChordChange(callback) {
    this.callbacks.onChordChange.push(callback);
  }

  /**
   * Remove chord change callback
   */
  offChordChange(callback) {
    const index = this.callbacks.onChordChange.indexOf(callback);
    if (index > -1) {
      this.callbacks.onChordChange.splice(index, 1);
    }
  }

  /**
   * Add beat callback
   */
  onBeat(callback) {
    this.callbacks.onBeat.push(callback);
  }

  /**
   * Remove beat callback
   */
  offBeat(callback) {
    const index = this.callbacks.onBeat.indexOf(callback);
    if (index > -1) {
      this.callbacks.onBeat.splice(index, 1);
    }
  }

  /**
   * Add bar callback
   */
  onBar(callback) {
    this.callbacks.onBar.push(callback);
  }

  /**
   * Remove bar callback
   */
  offBar(callback) {
    const index = this.callbacks.onBar.indexOf(callback);
    if (index > -1) {
      this.callbacks.onBar.splice(index, 1);
    }
  }

  /**
   * Add playing state change callback
   */
  onPlayingStateChange(callback) {
    this.callbacks.onPlayingStateChange.push(callback);
  }

  /**
   * Remove playing state change callback
   */
  offPlayingStateChange(callback) {
    const index = this.callbacks.onPlayingStateChange.indexOf(callback);
    if (index > -1) {
      this.callbacks.onPlayingStateChange.splice(index, 1);
    }
  }

  // === Private Callback Triggers ===

  triggerChordChangeCallbacks(chord, time, index) {
    this.callbacks.onChordChange.forEach(callback => {
      try {
        callback(chord, time, index);
      } catch (error) {
        console.error('[SynthPlayer] Error in chord change callback:', error);
      }
    });
  }

  triggerBeatCallbacks(beat, isDownbeat, time) {
    this.callbacks.onBeat.forEach(callback => {
      try {
        callback(beat, isDownbeat, time);
      } catch (error) {
        console.error('[SynthPlayer] Error in beat callback:', error);
      }
    });
  }

  triggerBarCallbacks(time) {
    this.callbacks.onBar.forEach(callback => {
      try {
        callback(time);
      } catch (error) {
        console.error('[SynthPlayer] Error in bar callback:', error);
      }
    });
  }

  triggerPlayingStateCallbacks(isPlaying) {
    this.callbacks.onPlayingStateChange.forEach(callback => {
      try {
        callback(isPlaying, this.playbackState);
      } catch (error) {
        console.error('[SynthPlayer] Error in playing state change callback:', error);
      }
    });
  }

  // === Utility Methods ===

  /**
   * Get current state
   */
  getState() {
    return {
      isPlaying: this.isPlaying,
      playbackState: this.playbackState,
      bpm: this.bpm,
      timeSignature: this.timeSignature,
      barsPerChord: this.barsPerChord,
      metronomeEnabled: this.metronomeEnabled,
      metronomeVisualOnly: this.metronomeVisualOnly,
      metronomeVolume: this.metronomeVolume,
      currentBeat: this.currentBeat,
      beatsPerBar: this.beatsPerBar,
      progressionIndex: this.progressionIndex,
      currentChord: this.currentChord,
      chordProgression: this.chordProgression
    };
  }

  /**
   * Tap tempo functionality
   */
  tap() {
    // Simple implementation - can be enhanced
    const now = Date.now();
    if (!this.tapTimes) this.tapTimes = [];
    
    this.tapTimes.push(now);
    
    // Keep only recent taps
    if (this.tapTimes.length > 8) {
      this.tapTimes.shift();
    }
    
    // Need at least 2 taps to calculate tempo
    if (this.tapTimes.length >= 2) {
      const intervals = [];
      for (let i = 1; i < this.tapTimes.length; i++) {
        intervals.push(this.tapTimes[i] - this.tapTimes[i - 1]);
      }
      
      const averageInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const calculatedBPM = Math.round(60000 / averageInterval);
      
      // Only accept reasonable BPM values
      if (calculatedBPM >= 40 && calculatedBPM <= 200) {
        // Don't call setBPM here - let the caller (App.js) handle it
        // This avoids double updates when the state change triggers useEffect
        return calculatedBPM;
      }
    }
    
    return this.bpm;
  }

  /**
   * Reset tap tempo
   */
  resetTap() {
    this.tapTimes = [];
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this.mixBus) this.mixBus.dispose();
    
    this.stop();
    
    if (this.clickOscillator) {
      this.clickOscillator.dispose();
    }
    if (this.accentOscillator) {
      this.accentOscillator.dispose();
    }
    if (this.clickEnvelope) {
      this.clickEnvelope.dispose();
    }
    if (this.accentEnvelope) {
      this.accentEnvelope.dispose();
    }
    if (this.gainNode) {
      this.gainNode.dispose();
    }
    
    // Clear all callbacks
    this.callbacks = {
      onChordChange: [],
      onBeat: [],
      onBar: [],
      onPlayingStateChange: []
    };
    
    this.initialized = false;
  }
}

// Export a singleton instance
const synthPlayer = new SynthPlayer();
export default synthPlayer;
